import { useCallback, useEffect, useRef, useState } from 'react'
import { ReadyState } from 'react-use-websocket'
import { v4 as uuidv4 } from 'uuid'

import { isRecord } from '@utils/json'

import { safeLocalStorage, safeSessionStorage } from '@hooks/useStorage'

import type {
  TabCoordinatorMessage,
  WebSocketLifecycleEvent,
  WebSocketMessage,
  WebSocketOutboundPayload,
} from './types'

const COORDINATOR_CHANNEL_NAME = 'tmca:websocket:coordination'
const TAB_ID_CHANNEL_NAME = 'tmca:websocket:tab-id'
const TAB_ID_STORAGE_KEY = 'tmca:websocket:tab-id'
const LEADER_LOCK_NAME = 'tmca:websocket:leader'
const LEADER_STORAGE_KEY = 'tmca:websocket:leader'
const LEADER_HEARTBEAT_INTERVAL_MS = 2000
const LEADER_STALE_TIMEOUT_MS = 6000

type TabClaimMessage = {
  instanceId: string
  tabId: string
  type: 'occupied' | 'probe'
}

type LeaderStorageRecord = {
  instanceId: string
  tabId: string
  updatedAt: number
}

type LockManagerLike = {
  request: (
    name: string,
    options: { ifAvailable: true },
    callback: (lock: unknown | null) => Promise<void> | void
  ) => Promise<void>
}

type StartNavigatorLockLeadershipOptions = {
  instanceId: string
  locks: LockManagerLike
  setLeadership: (isLeader: boolean) => void
}

const readOrCreateTabId = (): string => {
  if (typeof window === 'undefined') return uuidv4()

  const existingValue = safeSessionStorage.getItem(TAB_ID_STORAGE_KEY)
  if (existingValue) return existingValue

  const nextValue = uuidv4()
  safeSessionStorage.setItem(TAB_ID_STORAGE_KEY, nextValue)
  return nextValue
}

const isTabClaimMessage = (value: unknown): value is TabClaimMessage => {
  if (!isRecord(value)) return false

  return (
    (value.type === 'probe' || value.type === 'occupied') &&
    typeof value.instanceId === 'string' &&
    typeof value.tabId === 'string'
  )
}

const isLifecycleEvent = (value: unknown): value is WebSocketLifecycleEvent => {
  return value === 'open' || value === 'close' || value === 'error' || value === 'reconnect-stop'
}

const isTabCoordinatorMessage = (value: unknown): value is TabCoordinatorMessage => {
  if (!isRecord(value)) return false

  const hasBaseMessageFields =
    typeof value.sourceInstanceId === 'string' &&
    typeof value.sourceTabId === 'string' &&
    typeof value.sentAt === 'number' &&
    typeof value.type === 'string'

  if (!hasBaseMessageFields || !isRecord(value.payload)) return false

  switch (value.type) {
    case 'incoming-message':
      return isRecord(value.payload.message)
    case 'outbound-message':
      return (
        (value.payload.kind === 'text' || value.payload.kind === 'json') &&
        'message' in value.payload &&
        (value.payload.keep === undefined || typeof value.payload.keep === 'boolean')
      )
    case 'tick-subscriptions':
      return (
        Array.isArray(value.payload.symbols) &&
        value.payload.symbols.every((symbol) => typeof symbol === 'string')
      )
    case 'ready-state':
      return typeof value.payload.readyState === 'number'
    case 'ready-state-request':
      return true
    case 'leader-heartbeat':
    case 'leader-resigned':
      return typeof value.payload.leaderTabId === 'string'
    case 'lifecycle':
      return (
        isLifecycleEvent(value.payload.event) &&
        (value.payload.code === undefined || typeof value.payload.code === 'number')
      )
    default:
      return false
  }
}

const readLeaderRecord = (): LeaderStorageRecord | null => {
  const storedValue = safeLocalStorage.getJSON<unknown>(LEADER_STORAGE_KEY)
  if (
    !isRecord(storedValue) ||
    typeof storedValue.instanceId !== 'string' ||
    typeof storedValue.tabId !== 'string' ||
    typeof storedValue.updatedAt !== 'number'
  ) {
    return null
  }

  return storedValue as LeaderStorageRecord
}

const writeLeaderRecord = (record: LeaderStorageRecord): void => {
  safeLocalStorage.setJSON(LEADER_STORAGE_KEY, record)
}

const removeLeaderRecord = (instanceId: string): void => {
  const currentRecord = readLeaderRecord()

  if (currentRecord?.instanceId !== instanceId) {
    return
  }

  safeLocalStorage.removeItem(LEADER_STORAGE_KEY)
}

const isLeaderRecordStale = (record: LeaderStorageRecord | null): boolean => {
  return !record || Date.now() - record.updatedAt > LEADER_STALE_TIMEOUT_MS
}

const getNavigatorLocks = (): LockManagerLike | null => {
  if (typeof navigator === 'undefined') return null

  const navigatorWithLocks = navigator as Navigator & { locks?: LockManagerLike }

  return navigatorWithLocks.locks ?? null
}

const startNavigatorLockLeadership = ({
  instanceId,
  locks,
  setLeadership,
}: StartNavigatorLockLeadershipOptions): (() => void) => {
  const lockState: {
    stopped: boolean
    releaseLock?: () => void
    retryTimeoutId?: number
  } = {
    stopped: false,
  }

  const retryLater = () => {
    window.clearTimeout(lockState.retryTimeoutId)
    lockState.retryTimeoutId = window.setTimeout(() => {
      void requestLeadership()
    }, LEADER_HEARTBEAT_INTERVAL_MS)
  }

  const holdLeadership = () =>
    new Promise<void>((resolve) => {
      lockState.releaseLock = resolve
    })

  async function requestLeadership() {
    if (lockState.stopped) return

    try {
      await locks.request(LEADER_LOCK_NAME, { ifAvailable: true }, async (lock) => {
        if (!lock || lockState.stopped) {
          setLeadership(false)
          return
        }

        setLeadership(true)

        await holdLeadership()
      })
    } catch {
      if (!lockState.stopped) setLeadership(false)
    }

    if (lockState.stopped) return

    setLeadership(false)
    retryLater()
  }

  void requestLeadership()

  return () => {
    lockState.stopped = true
    window.clearTimeout(lockState.retryTimeoutId)
    lockState.releaseLock?.()
    removeLeaderRecord(instanceId)
    setLeadership(false)
  }
}

const useStableTabId = (): string => {
  const [tabId, setTabId] = useState(() => readOrCreateTabId())
  const instanceIdRef = useRef(uuidv4())

  useEffect(() => {
    if (typeof window === 'undefined') return

    safeSessionStorage.setItem(TAB_ID_STORAGE_KEY, tabId)
  }, [tabId])

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.BroadcastChannel === 'undefined') return

    const channel = new window.BroadcastChannel(TAB_ID_CHANNEL_NAME)

    const postClaimMessage = (type: TabClaimMessage['type'], candidateTabId = tabId) => {
      channel.postMessage({
        type,
        tabId: candidateTabId,
        instanceId: instanceIdRef.current,
      } satisfies TabClaimMessage)
    }

    channel.onmessage = (event: MessageEvent<unknown>) => {
      const message = event.data

      if (!isTabClaimMessage(message)) return

      if (message.instanceId === instanceIdRef.current || message.tabId !== tabId) return

      if (message.type === 'probe') return postClaimMessage('occupied')

      if (message.type === 'occupied') {
        setTabId((currentTabId) => (currentTabId === message.tabId ? uuidv4() : currentTabId))
      }
    }

    postClaimMessage('probe')

    return () => {
      channel.close()
    }
  }, [tabId])

  return tabId
}

export const useTabCoordinator = (enabled: boolean) => {
  const tabId = useStableTabId()
  const instanceIdRef = useRef(uuidv4())
  const channelRef = useRef<BroadcastChannel | null>(null)
  const listenersRef = useRef<Set<(message: TabCoordinatorMessage) => void>>(new Set())
  const isLeaderRef = useRef(false)
  const lastLeaderSeenAtRef = useRef(0)
  const leaderTabIdRef = useRef<string | null>(null)
  const [isLeaderTab, setIsLeaderTab] = useState(false)
  const [leaderTabId, setLeaderTabId] = useState<string | null>(null)

  const updateLeaderTabId = useCallback((nextLeaderTabId: string | null) => {
    leaderTabIdRef.current = nextLeaderTabId
    setLeaderTabId(nextLeaderTabId)
  }, [])

  const postCoordinatorMessage = useCallback(
    (message: Omit<TabCoordinatorMessage, 'sourceInstanceId' | 'sourceTabId' | 'sentAt'>) => {
      const coordinatorMessage = {
        ...message,
        sourceInstanceId: instanceIdRef.current,
        sourceTabId: tabId,
        sentAt: Date.now(),
      } as TabCoordinatorMessage

      channelRef.current?.postMessage(coordinatorMessage)
    },
    [tabId]
  )

  const setLeadership = useCallback(
    (nextIsLeader: boolean) => {
      if (isLeaderRef.current === nextIsLeader) return

      isLeaderRef.current = nextIsLeader
      setIsLeaderTab(nextIsLeader)

      if (nextIsLeader) {
        updateLeaderTabId(tabId)
        lastLeaderSeenAtRef.current = Date.now()
        return
      }

      if (leaderTabIdRef.current === tabId) updateLeaderTabId(null)

      postCoordinatorMessage({
        type: 'leader-resigned',
        payload: {
          leaderTabId: tabId,
        },
      })
    },
    [postCoordinatorMessage, tabId, updateLeaderTabId]
  )

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.BroadcastChannel === 'undefined') {
      setLeadership(enabled)
      return
    }

    const channel = new window.BroadcastChannel(COORDINATOR_CHANNEL_NAME)
    channelRef.current = channel

    channel.onmessage = (event: MessageEvent<unknown>) => {
      const message = event.data

      if (!isTabCoordinatorMessage(message)) return

      if (message.sourceInstanceId === instanceIdRef.current) return

      if (message.type === 'leader-heartbeat') {
        lastLeaderSeenAtRef.current = Date.now()
        updateLeaderTabId(message.payload.leaderTabId)
      }

      if (
        message.type === 'leader-resigned' &&
        leaderTabIdRef.current === message.payload.leaderTabId
      ) {
        updateLeaderTabId(null)
      }

      listenersRef.current.forEach((listener) => listener(message))
    }

    return () => {
      channel.close()
      channelRef.current = null
    }
  }, [enabled, setLeadership, updateLeaderTabId])

  useEffect(() => {
    if (!enabled) {
      setLeadership(false)
      return
    }

    const locks = getNavigatorLocks()

    if (!locks) {
      return
    }

    return startNavigatorLockLeadership({
      instanceId: instanceIdRef.current,
      locks,
      setLeadership,
    })
  }, [enabled, setLeadership])

  useEffect(() => {
    if (!enabled || getNavigatorLocks()) {
      return
    }

    const instanceId = instanceIdRef.current

    const claimFallbackLeadership = () => {
      const currentRecord = readLeaderRecord()

      if (
        currentRecord &&
        !isLeaderRecordStale(currentRecord) &&
        currentRecord.instanceId !== instanceId
      ) {
        setLeadership(false)
        lastLeaderSeenAtRef.current = currentRecord.updatedAt
        updateLeaderTabId(currentRecord.tabId)
        return
      }

      const nextRecord: LeaderStorageRecord = {
        instanceId,
        tabId,
        updatedAt: Date.now(),
      }

      writeLeaderRecord(nextRecord)
      const confirmedRecord = readLeaderRecord()
      const claimedLeadership = confirmedRecord?.instanceId === instanceId

      setLeadership(claimedLeadership)
      updateLeaderTabId(claimedLeadership ? tabId : confirmedRecord?.tabId ?? null)
    }

    claimFallbackLeadership()

    const intervalId = window.setInterval(claimFallbackLeadership, LEADER_HEARTBEAT_INTERVAL_MS)

    return () => {
      window.clearInterval(intervalId)
      removeLeaderRecord(instanceId)
      setLeadership(false)
    }
  }, [enabled, setLeadership, tabId, updateLeaderTabId])

  useEffect(() => {
    if (!enabled || !isLeaderTab) {
      return
    }

    const sendLeaderHeartbeat = () => {
      lastLeaderSeenAtRef.current = Date.now()
      writeLeaderRecord({
        instanceId: instanceIdRef.current,
        tabId,
        updatedAt: Date.now(),
      })
      postCoordinatorMessage({
        type: 'leader-heartbeat',
        payload: {
          leaderTabId: tabId,
        },
      })
    }

    sendLeaderHeartbeat()

    const heartbeatIntervalId = window.setInterval(
      sendLeaderHeartbeat,
      LEADER_HEARTBEAT_INTERVAL_MS
    )

    return () => {
      window.clearInterval(heartbeatIntervalId)
    }
  }, [enabled, isLeaderTab, postCoordinatorMessage, tabId])

  useEffect(() => {
    if (!enabled || isLeaderTab) {
      return
    }

    const staleLeaderIntervalId = window.setInterval(() => {
      if (Date.now() - lastLeaderSeenAtRef.current > LEADER_STALE_TIMEOUT_MS) {
        updateLeaderTabId(null)
      }
    }, LEADER_HEARTBEAT_INTERVAL_MS)

    return () => {
      window.clearInterval(staleLeaderIntervalId)
    }
  }, [enabled, isLeaderTab, updateLeaderTabId])

  useEffect(() => {
    if (!enabled) {
      updateLeaderTabId(null)
    }
  }, [enabled, updateLeaderTabId])

  const subscribe = useCallback((listener: (message: TabCoordinatorMessage) => void) => {
    listenersRef.current.add(listener)

    return () => {
      listenersRef.current.delete(listener)
    }
  }, [])

  const sendToLeader = useCallback(
    (payload: WebSocketOutboundPayload) => {
      postCoordinatorMessage({
        type: 'outbound-message',
        payload,
      })
    },
    [postCoordinatorMessage]
  )

  const broadcastIncomingMessage = useCallback(
    (message: WebSocketMessage) => {
      postCoordinatorMessage({
        type: 'incoming-message',
        payload: {
          message,
        },
      })
    },
    [postCoordinatorMessage]
  )

  const broadcastReadyState = useCallback(
    (readyState: ReadyState) => {
      postCoordinatorMessage({
        type: 'ready-state',
        payload: {
          readyState,
        },
      })
    },
    [postCoordinatorMessage]
  )

  const broadcastTickSubscriptions = useCallback(
    (symbols: string[]) => {
      postCoordinatorMessage({
        type: 'tick-subscriptions',
        payload: { symbols },
      })
    },
    [postCoordinatorMessage]
  )

  const requestReadyState = useCallback(() => {
    postCoordinatorMessage({
      type: 'ready-state-request',
      payload: {},
    })
  }, [postCoordinatorMessage])

  const broadcastLifecycleEvent = useCallback(
    (event: WebSocketLifecycleEvent, code?: number) => {
      postCoordinatorMessage({
        type: 'lifecycle',
        payload: {
          event,
          code,
        },
      })
    },
    [postCoordinatorMessage]
  )

  return {
    broadcastIncomingMessage,
    broadcastLifecycleEvent,
    broadcastReadyState,
    broadcastTickSubscriptions,
    isLeaderTab,
    leaderTabId,
    requestReadyState,
    sendToLeader,
    subscribe,
    tabId,
  }
}
